import { PAGE_BREAKPOINTS } from '@cat-ecom/pcc-components/src/constants/commonConstants';
import { useDealerLocatorLoader } from 'components/dealer-locator/dealer-locator-loader-context';
import { useDealerLocatorModal } from 'components/dealer-locator/dealer-locator-modal-context';
import usePlaces from 'components/dealer-locator/hooks/use-places';
import { StoreAddress } from 'components/dealer-locator/types';
import {
  Conditional,
  DealerLocatorSearchForm as DealerLocatorSearchFormBlocks,
  DealerLocatorSearchSuggestions,
  useBreakpoint
} from 'components/pcc-components';
import { HEADLESS_HOME_PAGE } from 'constants/links';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { usePathMatch } from 'hooks/use-path-match';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useEsiteCtx } from 'services/esite/context';
import { useDealerLocatorDrawer } from '../../dealer-locator-drawer-context';
import {
  DEALER_LOCATOR_EVENT_NAME,
  DEFAULT_NUM_RESULTS,
  FORM_LOCATION_HOMEPAGE,
  SELECTED_STORE_ID,
  TERMS_CHECKED
} from '../constants';
import { getLocationAddress } from '../utils';

import {
  PCC_STORE_LOCATION_GEOLOCATIONPICKUP,
  PCC_STORELOCATION_CATCORP_USER,
  PCC_STORELOCATION_GEOLOCATIONSTATE_VISIBILITY
} from 'constants/featureFlag';
import { useParams } from 'next/navigation';
import { useFeatureFlagQuery } from 'services/featureFlag/useFeatureFlagQuery';
interface DealerLocatorSearchFormProps {
  autoSearch?: boolean;
  location: string;
  setLocation: (search: string) => void;
  getLocations: (search: string) => void;
  setNumDisplayed: (num: number) => void;
  isModal: boolean;
  isSearchLoading?: boolean;
  resetLocationsAndMapToggle: () => void;
  resetFilters: () => void;
  setIsSelectStoreError: (value: boolean) => void;
  storeAddress?: StoreAddress;
  isMobile?: boolean;
  isStoreLocationMapViewUpdateFlag?: boolean;
  isCatCSR?: boolean;
}

export const DealerLocatorSearchForm = ({
  autoSearch,
  location,
  setLocation,
  getLocations,
  setNumDisplayed,
  isModal,
  isSearchLoading,
  resetLocationsAndMapToggle,
  resetFilters,
  setIsSelectStoreError,
  storeAddress,
  isMobile,
  isStoreLocationMapViewUpdateFlag,
  isCatCSR
}: DealerLocatorSearchFormProps) => {
  const { fireEvent } = useFormSubmitEvent();
  const { userCountryCode, userCity, userRegionCode } = useEsiteCtx();
  const { esite } = useParams();
  const context = {
    kind: 'location',
    key: esite,
    esite: esite
  };

  const { data: storeCatcorpFlagData = {} } = useFeatureFlagQuery(
    PCC_STORELOCATION_CATCORP_USER,
    context
  );
  const isStoreCatCorpFlag =
    storeCatcorpFlagData?.[PCC_STORELOCATION_CATCORP_USER] ?? false;

  const { data: storeGeoLocationFlagData = {} } = useFeatureFlagQuery(
    PCC_STORE_LOCATION_GEOLOCATIONPICKUP,
    context
  );

  const isStoreGeoLocationFlag =
    storeGeoLocationFlagData?.[PCC_STORE_LOCATION_GEOLOCATIONPICKUP] ?? false;
  const { data: storeGeoLocationWithStateVisibilityFlagData = {} } =
    useFeatureFlagQuery(PCC_STORELOCATION_GEOLOCATIONSTATE_VISIBILITY, context);

  const isStoreGeoLocationWithStateVisibilityFlag =
    storeGeoLocationWithStateVisibilityFlagData?.[
      PCC_STORELOCATION_GEOLOCATIONSTATE_VISIBILITY
    ] ?? false;

  const t = useTranslations();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const dealerLocatorModal = useDealerLocatorModal();
  const { isDealerLocatorLoading, setIsDealerLocatorLoading } =
    useDealerLocatorLoader();
  const { resetField, setValue } = useFormContext();
  const { getPlaces, suggestions } = usePlaces();
  const isHomePage = usePathMatch(HEADLESS_HOME_PAGE);
  const isFirstTimeOpened = useRef(true);
  const hasLocationBeenCleared = useRef(true);
  const currentUserLocation = useMemo(() => {
    if (!userCity || !userCountryCode) return '';

    const userState =
      isStoreGeoLocationWithStateVisibilityFlag && userRegionCode
        ? `${userRegionCode}, `
        : '';

    return `${userCity}, ${userState}${userCountryCode}`;
  }, [
    userCity,
    userCountryCode,
    isStoreGeoLocationWithStateVisibilityFlag,
    userRegionCode
  ]);

  const [isInputError, setIsInputError] = useState(false);

  const isDrawerOrModalOpen = isModal
    ? dealerLocatorModal?.modalControl.isActive
    : dealerLocatorDrawer?.isDrawerOpen;
  const locationSelected = autoSearch
    ? getLocationAddress(storeAddress)
    : undefined;

  const onSearchHandler = useCallback(
    (searchText?: string) => {
      if (searchText?.trim() === '') {
        setLocation('');
        setIsInputError(true);
      } else {
        resetField(SELECTED_STORE_ID);
        setNumDisplayed(DEFAULT_NUM_RESULTS);
        fireEvent({
          formName: DEALER_LOCATOR_EVENT_NAME,
          formContent: searchText,
          formLocation: isHomePage ? FORM_LOCATION_HOMEPAGE : ''
        });
        setLocation(searchText);
        getLocations(searchText);
      }
    },
    [
      setLocation,
      resetField,
      setNumDisplayed,
      fireEvent,
      isHomePage,
      getLocations
    ]
  );

  const onKeyPressHandler = (text: string) => {
    setLocation(text);
    if (text) {
      getPlaces(text);
    }
  };

  const resetDealerLocator = useCallback(
    (shouldResetFilters?: boolean) => {
      resetLocationsAndMapToggle();
      if (
        hasLocationBeenCleared.current &&
        locationSelected &&
        isStoreGeoLocationFlag
      ) {
        setLocation(currentUserLocation);
        getLocations(currentUserLocation);
      } else {
        setLocation('');
      }
      hasLocationBeenCleared.current = false;
      setIsInputError(false);
      setNumDisplayed(DEFAULT_NUM_RESULTS);
      setIsDealerLocatorLoading(false);
      resetField(SELECTED_STORE_ID);
      setValue(TERMS_CHECKED, isCatCSR);
      setIsSelectStoreError(false);
      if (shouldResetFilters) {
        resetFilters();
      }
    },
    [
      resetLocationsAndMapToggle,
      locationSelected,
      setNumDisplayed,
      setIsDealerLocatorLoading,
      resetField,
      setValue,
      setIsSelectStoreError,
      setLocation,
      currentUserLocation,
      resetFilters,
      isStoreGeoLocationFlag,
      getLocations,
      isCatCSR
    ]
  );

  //Set termsChecked to true by default if user is CSR
  useEffect(() => {
    if (isCatCSR) {
      setValue(TERMS_CHECKED, true);
    }
  }, [isCatCSR, setValue]);

  // Reset drawer onClose
  useEffect(() => {
    if (!isDrawerOrModalOpen) {
      resetDealerLocator(true);
    }
  }, [isDrawerOrModalOpen, resetDealerLocator]);

  useEffect(() => {
    if (
      isDrawerOrModalOpen &&
      isFirstTimeOpened.current &&
      isStoreCatCorpFlag &&
      isStoreGeoLocationFlag
    ) {
      isFirstTimeOpened.current = false;
      if (!locationSelected) {
        setLocation(currentUserLocation);
        if (currentUserLocation) {
          getLocations(currentUserLocation);
        }
        isFirstTimeOpened.current = false;
      }
    }
  }, [
    isDrawerOrModalOpen,
    locationSelected,
    setLocation,
    getLocations,
    currentUserLocation,
    isStoreCatCorpFlag,
    isStoreGeoLocationFlag
  ]);
  return (
    <DealerLocatorSearchFormBlocks
      isVisible={isDrawerOrModalOpen}
      placeholder={isMD ? t('ENTER_LOCATION') : undefined}
      value={location}
      onSearch={onSearchHandler}
      onKeyPress={onKeyPressHandler}
      isInputError={isInputError}
      setIsInputError={setIsInputError}
      onCancel={resetDealerLocator}
      isSearchLoading={isSearchLoading}
      isDisabled={isDealerLocatorLoading}
      locationSelected={locationSelected}
      mobileBreakPoint={0}
      isMobile={isMobile}
      isStoreLocationMapViewUpdateFlag={isStoreLocationMapViewUpdateFlag}
    >
      <Conditional test={isInputError}>
        <span slot="error" className="mt-1">
          {t('FYD_INVALID_LOCATION')}
        </span>
      </Conditional>
      <DealerLocatorSearchSuggestions
        search={location}
        suggestions={suggestions}
      />
    </DealerLocatorSearchFormBlocks>
  );
};
